import classNames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { Icons, Images } from "assets";
import { getEarningBookSalesService } from "shared/services/projectHeadService";
import styles from "./style.module.scss";

interface BookSalesModalProps {
  show: boolean;
  handleClose: () => void;
  /** The transaction / earning row the sales belong to */
  item: any;
  /**
   * Overrides how the sales are fetched. Defaults to the project-head
   * endpoint; the accountant view passes its own earning-based fetcher.
   */
  fetchSales?: (earningId: any, type?: string) => Promise<any>;
}

// B2C first — it is the book-level view partners look for.
const Tabs = ["B2C (Users)", "B2B (Schools)"];
const typeByTab: Record<string, string> = {
  "B2C (Users)": "b2c",
  "B2B (Schools)": "b2b",
};

const SKELETON_ROWS = 5;

// This product is PKR-based; only switch to "$" when the API explicitly
// flags an international (USD) currency.
const getCurrencySymbol = (currencyType?: string) =>
  currencyType === "USD" || currencyType === "$" ? "$" : "Rs.";

const formatAmount = (value: any) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const BookSalesModal = ({
  show,
  handleClose,
  item,
  fetchSales,
}: BookSalesModalProps) => {
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // The two tabs have different column counts, so a scroll offset carried
  // over from one tab leaves the other's first column cut off.
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const isB2B = typeByTab[activeTab] === "b2b";

  // The transactions list keys each row by `earning_id`; fall back to `id`
  // for any caller that already passes an earning object.
  const earningId = item?.earning_id ?? item?.id;

  const handleActiveTab = (val: string) => {
    setActiveTab(val);
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = 0;
      tableWrapperRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (!show || !earningId) return;

    const type = typeByTab[activeTab];
    const request = fetchSales
      ? fetchSales(earningId, type)
      : getEarningBookSalesService(earningId, type);
    setLoading(true);
    request
      .then(({ data }: any) => {
        // Backend shape: { data: { data: rows[], summary: {} }, status }
        const payload = data?.data;
        setRows(payload?.data || []);
        setSummary(payload?.summary || null);
      })
      .catch((err) => {
        console.error("Failed to load book sales", err);
        setRows([]);
        setSummary(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [show, earningId, activeTab]);

  const onClose = () => {
    handleClose?.();
    setActiveTab(Tabs[0]);
    setRows([]);
    setSummary(null);
  };

  const currency = getCurrencySymbol(
    summary?.currency_type || item?.currency_type
  );

  const formatDate = (date: any) =>
    date ? moment(date).format("DD MMM YYYY") : "----";

  /* ---------------- Column widths (per tab) ---------------- */

  // Explicit widths keep the fixed-layout header and body on the same grid;
  // without them all seven columns collapse to equal width and the skeleton
  // bars bleed across columns.
  const renderColGroup = () =>
    isB2B ? (
      <colgroup>
        <col style={{ width: "24%" }} />
        <col style={{ width: "20%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "11%" }} />
      </colgroup>
    ) : (
      <colgroup>
        <col style={{ width: "27%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "12%" }} />
      </colgroup>
    );

  /* ---------------- Table head (per tab) ---------------- */

  const renderHead = () =>
    isB2B ? (
      <tr>
        <th className={styles.th}>School</th>
        <th className={styles.th}>Plan</th>
        <th className={styles.th}>Date</th>
        <th className={styles.th}>Books</th>
        <th className={styles.th}>School Paid</th>
        <th className={styles.th}>Earned</th>
        <th className={styles.th}>Platform</th>
      </tr>
    ) : (
      <tr>
        <th className={styles.th}>Book</th>
        <th className={styles.th}>Date</th>
        <th className={styles.th}>Type</th>
        <th className={styles.th}>Reader Paid</th>
        <th className={styles.th}>Coins Earned</th>
        <th className={styles.th}>Earned</th>
        <th className={styles.th}>Platform</th>
      </tr>
    );

  /* ---------------- Skeleton (mirrors each tab's real row) ---------------- */

  const renderSkeletonRows = () =>
    [...Array(SKELETON_ROWS)].map((_, index) => (
      <tr key={`skeleton-${index}`}>
        {isB2B ? (
          <>
            <td className={styles.td}>
              <span className={classNames(styles.shimmer, styles.skeletonText)} />
            </td>
            <td className={styles.td}>
              <span className={classNames(styles.shimmer, styles.skeletonText)} />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextXs)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
          </>
        ) : (
          <>
            <td className={styles.td}>
              <div className={styles.mediaCell}>
                <span
                  className={classNames(styles.shimmer, styles.skeletonThumb)}
                />
                <span
                  className={classNames(styles.shimmer, styles.skeletonText)}
                />
              </div>
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span className={classNames(styles.shimmer, styles.skeletonChip)} />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
            <td className={styles.td}>
              <span
                className={classNames(styles.shimmer, styles.skeletonTextSm)}
              />
            </td>
          </>
        )}
      </tr>
    ));

  /* ---------------- Real rows ---------------- */

  const renderRows = () =>
    rows.map((row: any, index: number) =>
      isB2B ? (
        <tr key={`b2b-${row?.plan_id}-${index}`}>
          <td className={styles.td}>
            <span className={styles.cellTitle}>
              {row?.school_name || "----"}
            </span>
          </td>
          <td className={styles.td}>
            <span className={styles.cellMuted}>{row?.plan_name || "----"}</span>
          </td>
          <td className={styles.td}>
            <span className={styles.cellMuted}>{formatDate(row?.date)}</span>
          </td>
          <td className={styles.td}>{row?.book_count ?? 0}</td>
          <td className={styles.td}>
            {currency} {formatAmount(row?.school_paid)}
          </td>
          <td className={classNames(styles.td, styles.earned)}>
            {currency} {formatAmount(row?.earned_amount)}
          </td>
          <td className={classNames(styles.td, styles.mutedCell)}>
            {currency} {formatAmount(row?.platform_amount)}
          </td>
        </tr>
      ) : (
        <tr key={`b2c-${row?.book_id}-${index}`}>
          <td className={styles.td}>
            <div className={styles.mediaCell}>
              <img
                src={row?.book_thumbnail || Images.DefaultBookImg}
                alt=""
                className={styles.thumbnail}
                onError={(e) => {
                  // Dead S3 links would otherwise render as broken-image text.
                  e.currentTarget.src = Images.DefaultBookImg;
                }}
              />
              <span className={styles.cellTitle}>
                {row?.book_title || "----"}
              </span>
            </div>
          </td>
          <td className={styles.td}>
            <span className={styles.cellMuted}>{formatDate(row?.date)}</span>
          </td>
          <td className={styles.td}>
            <span
              className={classNames(
                styles.chip,
                row?.type === "borrow" ? styles.chipBorrow : styles.chipBuy
              )}
            >
              {row?.type || "----"}
            </span>
          </td>
          <td className={styles.td}>{formatAmount(row?.reader_paid_coins)}</td>
          <td className={styles.td}>{formatAmount(row?.earned_coins)}</td>
          <td className={classNames(styles.td, styles.earned)}>
            {currency} {formatAmount(row?.earned_amount)}
          </td>
          <td className={classNames(styles.td, styles.mutedCell)}>
            {currency} {formatAmount(row?.platform_amount)}
          </td>
        </tr>
      )
    );

  return (
    <Dialog
      open={show}
      onClose={onClose}
      aria-labelledby="book-sales-modal"
      maxWidth="md"
      PaperProps={{ className: styles.dialogPaper }}
    >
      <div className={styles.body}>
        <div className={styles.header}>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <Icons.CrossIcon />
          </button>
        </div>

        <div className={styles.titleBlock}>
          <span className={styles.modalTitle}>
            Sales Breakdown{" "}
            {item?.transaction_id ? (
              <span className={styles.modalSubTitle}>
                ({item?.transaction_id})
              </span>
            ) : null}
          </span>
        </div>

        {/* Summary */}
        {summary ? (
          <div className={styles.summaryGrid}>
            <div className={classNames(styles.summaryItem, styles.summaryMain)}>
              <span className={styles.summaryLabel}>Total Earned</span>
              <span className={styles.summaryMainValue}>
                {currency} {formatAmount(summary?.earned_amount)}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>B2C (Users)</span>
              <span className={styles.summaryValue}>
                {currency} {formatAmount(summary?.b2c_earned_amount)}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>B2B (Schools)</span>
              <span className={styles.summaryValue}>
                {currency} {formatAmount(summary?.b2b_earned_amount)}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Platform Deduction</span>
              <span className={styles.summaryValue}>
                {currency} {formatAmount(summary?.platform_amount)}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Coins Earned</span>
              <span className={styles.summaryValue}>
                {formatAmount(summary?.earned_coins)}
              </span>
            </div>
          </div>
        ) : null}

        <div className={styles.tabsWrap}>
          {Tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={classNames(
                styles.tabBtn,
                activeTab === tab && styles.tabBtnActive
              )}
              onClick={() => handleActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className={styles.tableWrapper} ref={tableWrapperRef}>
          {!loading && !rows?.length ? (
            <div className={styles.emptyState}>
              {isB2B
                ? "No school sales in this transaction."
                : "No user sales in this transaction."}
            </div>
          ) : (
            <table className={styles.table}>
              {renderColGroup()}
              <thead>{renderHead()}</thead>
              <tbody>{loading ? renderSkeletonRows() : renderRows()}</tbody>
            </table>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default BookSalesModal;
